import { useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalculator, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../utils/apiClient";
import { initialConsignment } from "../constants";

export default function RuleCalculator() {
  const [open, setOpen] = useState(false);
  const [consignment, setConsignment] = useState(initialConsignment);
  const [result, setResult] = useState(null);

  const handleEvaluate = async () => {
    try {
      const response = await apiClient.post("/api/v1/difotTools/evaluate", {
        consignment: {
          ...consignment,
          weight: consignment.weight === "" ? null : Number(consignment.weight),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Failed to evaluate rules");
      }
      const data = await response.json();
      setResult(data.result || null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const boolFields = [
    { key: "dangerousGoods", label: "Dangerous Goods" },
    { key: "handUnloading", label: "Hand Unloading" },
    { key: "tailLiftRequired", label: "Tail Lift Required" },
    { key: "appointmentRequired", label: "Appointment Required" },
  ];

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-md ring-1 ring-gray-200/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faCalculator} className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-900">Rule Calculator</span>
          <span className="text-xs text-gray-400">Test a consignment against active rules</span>
        </div>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="w-3.5 h-3.5 text-gray-400" />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-4">
            {[
              { key: "customerName", label: "Customer Name", type: "text" },
              { key: "companyName", label: "Company Name", type: "text" },
              { key: "toName", label: "To Name", type: "text" },
              { key: "storeName", label: "Store Name", type: "text" },
              { key: "state", label: "State", type: "text" },
              { key: "postcode", label: "Postcode", type: "text" },
              { key: "carrier", label: "Carrier", type: "text" },
              { key: "serviceType", label: "Service Type", type: "text" },
              { key: "weight", label: "Weight", type: "number" },
              { key: "despatchDate", label: "Despatch Date", type: "date" },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">
                  {label}
                </label>
                <input
                  type={type}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677]"
                  value={consignment[key]}
                  onChange={(e) =>
                    setConsignment((p) => ({
                      ...p,
                      [key]: key === "state" ? e.target.value.toUpperCase() : e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {boolFields.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 accent-[#144677]"
                  checked={consignment[key]}
                  onChange={(e) => setConsignment((p) => ({ ...p, [key]: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
            <button
              type="button"
              onClick={handleEvaluate}
              className="ml-auto px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              Evaluate
            </button>
          </div>
          {result && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-1">
              <p className="text-sm font-semibold text-emerald-900">
                Final delivery:{" "}
                <span className="text-emerald-700">
                  {result.finalDays} day{result.finalDays !== 1 ? "s" : ""}
                </span>
                <span className="text-emerald-600 font-normal text-xs ml-2">
                  (base {result.baseDays} + adjustment {result.adjustmentDays})
                </span>
              </p>
              <p className="text-xs text-emerald-700">
                Applied:{" "}
                {(result.appliedRules || []).length > 0
                  ? (result.appliedRules || []).map((r) => r.name).join(", ")
                  : <span className="italic">no matching rules</span>}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
